// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
// ── Account-data access, export and disclosure (Slice C, AC4) ──
/**
 * The organisation's decision on a data operation, as recorded by the person
 * who decided it. The platform records the decision; it does not make it.
 */
export const ACCOUNT_DATA_DISCLOSURE_DECISIONS = [
    'approved',
    'approved_with_conditions',
    'refused',
];
/**
 * The decisions on which the article a basis rests on must be recorded.
 *
 * Sprint 62.27.0 gate E12.5 pass 3 (N1). The installed overlay form declares
 * `lawful_basis_citation` optional with rule `require_basis_citation_when_approved`
 * over exactly these values: a citation is owed where the organisation relies
 * on a basis to approve an operation, and not on a refusal, which may record
 * that no basis exists. The instrument the register serves enumerates no basis.
 * A test holds this set equal to that rule's `value` list.
 */
export const ACCOUNT_DATA_DECISIONS_REQUIRING_CITATION = [
    'approved',
    'approved_with_conditions',
];
/** Whether a decision owes the article its basis rests on. */
export function accountDataDecisionRequiresCitation(decision) {
    return ACCOUNT_DATA_DECISIONS_REQUIRING_CITATION.includes(decision);
}
/**
 * fix-sprint-20261003-0625 F23 — the four module-local codes the intake offered
 * before Sprint 62.27.0 gate E12.5 pass 2 made the basis a statement. A record
 * may still hold one; nothing writes one now. Read only to render it.
 */
export const ACCOUNT_DATA_LEGACY_BASIS_CODES = [
    'investor_consent',
    'statutory_duty_or_authority_request',
    'contract_with_investor',
    'no_basis_identified',
];
/** Whether a stored `lawful_basis` is one of the legacy codes rather than a statement. */
export function isAccountDataLegacyBasisCode(value) {
    return ACCOUNT_DATA_LEGACY_BASIS_CODES.includes(value);
}
/** The shortest `justification_text` the boundary accepts (characters, after trim). */
export const ACCOUNT_DATA_JUSTIFICATION_MIN_LENGTH = 20;
/** The longest `lawful_basis` statement the boundary accepts (characters). */
export const ACCOUNT_DATA_LAWFUL_BASIS_MAX_LENGTH = 2000;
/** The longest `lawful_basis_citation` the boundary accepts (characters). */
export const ACCOUNT_DATA_LAWFUL_BASIS_CITATION_MAX_LENGTH = 500;
// ── Regulatory disclosures and the prospectus (Slice D, AC5) ──
export const REGULATORY_DISCLOSURE_KINDS = ['regulatory', 'investor', 'prospectus'];
/**
 * `approved` requires a named approver; `published` requires `approved` AND at
 * least one publication evidence reference. Adding a version returns the
 * record to `draft` and clears the approver — an approval is of a version,
 * not of a title.
 */
export const REGULATORY_DISCLOSURE_STATUSES = ['draft', 'approved', 'published'];
// ── Collections ──
export const INVESTOR_VERIFICATION_EVIDENCE_COLLECTION = 'grc_investor_verification_evidence';
export const ACCOUNT_DATA_DISCLOSURES_COLLECTION = 'grc_account_data_disclosures';
export const REGULATORY_DISCLOSURES_COLLECTION = 'grc_regulatory_disclosures';
// ── Error codes ──
export const INVESTOR_PROTECTION_ERROR_CODES = {
    VERIFICATION_NOT_FOUND: 'INVESTOR_VERIFICATION_NOT_FOUND',
    /** `verified_at` / `occurred_at` carried no zone; refused at the boundary. */
    INSTANT_WITHOUT_ZONE: 'INVESTOR_PROTECTION_INSTANT_WITHOUT_ZONE',
    ACCOUNT_DATA_DISCLOSURE_NOT_FOUND: 'ACCOUNT_DATA_DISCLOSURE_NOT_FOUND',
    /** `justification_text` shorter than `ACCOUNT_DATA_JUSTIFICATION_MIN_LENGTH`, or `lawful_basis` absent. */
    ACCOUNT_DATA_JUSTIFICATION_REQUIRED: 'ACCOUNT_DATA_JUSTIFICATION_REQUIRED',
    DISCLOSURE_NOT_FOUND: 'REGULATORY_DISCLOSURE_NOT_FOUND',
    /** HTTP 409 — publish attempted with `approver_user_id` null (AC5). */
    DISCLOSURE_NOT_APPROVED: 'DISCLOSURE_NOT_APPROVED',
    /** Publish attempted with no publication evidence reference. */
    DISCLOSURE_PUBLICATION_EVIDENCE_REQUIRED: 'DISCLOSURE_PUBLICATION_EVIDENCE_REQUIRED',
    /** Approve attempted on a record that is not `draft`. */
    DISCLOSURE_NOT_DRAFT: 'DISCLOSURE_NOT_DRAFT',
    /**
     * HTTP 409 — a new version was added against a `current_version` that had
     * already moved on (a concurrent version won). The caller re-reads and
     * decides again; nothing was written.
     */
    DISCLOSURE_VERSION_CONFLICT: 'DISCLOSURE_VERSION_CONFLICT',
    /** The record cited a fine area no installed framework of the organisation declares. */
    FINE_AREA_NOT_INSTALLED: 'INVESTOR_PROTECTION_FINE_AREA_NOT_INSTALLED',
    /**
     * Sprint 62.25.0 (OD8 (a)) — the record cited a fine area of a framework the
     * organisation installed for REFERENCE only (or one the install-mode policy
     * otherwise excludes). It is installed, so "not installed" would be false;
     * `details.declaration_absence` names the case.
     */
    FINE_AREA_NOT_OPERATIONAL: 'INVESTOR_PROTECTION_FINE_AREA_NOT_OPERATIONAL',
    /** `aml_evidence_ref` named a payments-evidence record that does not exist for the organisation. */
    AML_EVIDENCE_REF_NOT_FOUND: 'INVESTOR_PROTECTION_AML_EVIDENCE_REF_NOT_FOUND',
};
//# sourceMappingURL=investor-protection.js.map